// src/pages/credits/CreditsPage.jsx
import { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import { getUserTransactions } from "../../services/firestoreService";
import AppLayout from "../../components/layout/AppLayout";
import styles from "./CreditsPage.module.css";
import { Search, Lock, Zap, Unlock, CreditCard, Gift } from "lucide-react";

const CHECKOUT_URL = 'https://us-central1-gigscout-90d97.cloudfunctions.net/createYocoCheckout';

const BUNDLES = [
  {
    id:        "starter",
    name:      "Starter",
    credits:   10,
    price:     50,
    popular:   false,
    perCredit: "R5.00",
    desc:      "5 gig unlocks — perfect to try it out",
  },
  {
    id:        "pro",
    name:      "Pro",
    credits:   20,
    price:     100,
    popular:   true,
    perCredit: "R5.00",
    desc:      "10 gig unlocks — best for active seekers",
  },
  {
    id:        "business",
    name:      "Power",
    credits:   60,
    price:     250,
    popular:   false,
    perCredit: "R4.17",
    desc:      "30 gig unlocks — best value for power users",
  },
];

function timeAgo(ts) {
  if (!ts) return "";
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  const diff  = Date.now() - date.getTime();
  const days  = Math.floor(diff / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7)  return `${days}d ago`;
  return date.toLocaleDateString("en-ZA", { day: "numeric", month: "short" });
}

export default function CreditsPage() {
  const { currentUser, credits } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loadingTx,    setLoadingTx]    = useState(true);
  const [purchasing,   setPurchasing]   = useState(null);
  const [buyError,     setBuyError]     = useState(null);

  useEffect(() => {
    if (!currentUser) return;
    getUserTransactions(currentUser.uid, 15)
      .then(setTransactions)
      .catch(() => {})
      .finally(() => setLoadingTx(false));
  }, [currentUser]);

  const handleBuy = async (bundle) => {
    setBuyError(null);
    setPurchasing(bundle.id);
    sessionStorage.setItem('creditsBefore', String(credits));
    try {
      const token = await currentUser.getIdToken();
      const resp  = await fetch(CHECKOUT_URL, {
        method:  'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ bundleId: bundle.id, appUrl: window.location.origin }),
      });
      const result = await resp.json();
      if (result.redirectUrl) {
        if (result.checkoutId) sessionStorage.setItem('checkoutId', result.checkoutId);
        window.location.href = result.redirectUrl;
      } else {
        throw new Error(result.error || 'No checkout URL returned');
      }
    } catch (err) {
      setBuyError(err.message || 'Payment failed. Please try again.');
      setPurchasing(null);
    }
  };

  return (
    <AppLayout>
      <div className={styles.page}>
        {/* ── Page header ── */}
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.title}>Credits</h1>
            <p className={styles.sub}>Use credits to unlock contact details and apply links on GigScout listings.</p>
          </div>
          <div className={styles.balanceCard}>
            <span className={styles.balanceLabel}>Your balance</span>
            <span className={styles.balanceValue}>{credits}</span>
            <span className={styles.balanceUnit}>credits</span>
          </div>
        </div>

        {/* ── How it works ── */}
        <div className={styles.howItWorks}>
          <div className={styles.step}>
            <div className={styles.stepIcon}><Search size={20} strokeWidth={2} /></div>
            <div className={styles.stepText}>
              <strong>Browse jobs for free</strong>
              <span>See all job titles, companies, and descriptions at no cost</span>
            </div>
          </div>
          <div className={styles.stepArrow}>→</div>
          <div className={styles.step}>
            <div className={styles.stepIcon}><Lock size={20} strokeWidth={2} /></div>
            <div className={styles.stepText}>
              <strong>2 credits to unlock</strong>
              <span>Spend 2 credits to reveal the apply link and contact details</span>
            </div>
          </div>
          <div className={styles.stepArrow}>→</div>
          <div className={styles.step}>
            <div className={styles.stepIcon}><Zap size={20} strokeWidth={2} /></div>
            <div className={styles.stepText}>
              <strong>Apply directly</strong>
              <span>Go straight to the original listing — no middleman</span>
            </div>
          </div>
        </div>

        {/* ── New users note ── */}
        {credits === 5 && transactions.length === 0 && (
          <div className={styles.freeNote}>
            🎉 You have <strong>5 free starter credits</strong> — use them to explore local gigs.
          </div>
        )}

        {/* ── Bundles ── */}
        <section>
          <h2 className={styles.sectionTitle}>Buy Credits</h2>
          <p className={styles.sectionSub}>Paid securely via <strong>Yoco</strong> · Instant delivery</p>
          <div className={styles.unlockExplainer}>
            <span className={styles.unlockEq}><Unlock size={14} strokeWidth={2.5} /> 1 unlock = 2 credits</span>
            <span className={styles.unlockDesc}>Each unlock reveals the apply link and employer contact for one listing.</span>
          </div>
          {buyError && <div className={styles.buyError}>{buyError}</div>}
          <div className={styles.bundles}>
            {BUNDLES.map(bundle => (
              <div key={bundle.id} className={`${styles.bundle} ${bundle.popular ? styles.bundlePopular : ""}`}>
                {bundle.popular && <div className={styles.popularBadge}>Most Popular</div>}
                <div className={styles.bundleName}>{bundle.name}</div>
                <div className={styles.bundleCredits}>
                  <span className={styles.bundleCreditsNum}>{bundle.credits}</span>
                  <span className={styles.bundleCreditsLabel}>credits</span>
                </div>
                <div className={styles.bundlePrice}>R{bundle.price}</div>
                <div className={styles.bundlePerCredit}>{bundle.perCredit} per credit</div>
                <p className={styles.bundleDesc}>{bundle.desc}</p>
                <button
                  className={`${styles.buyBtn} ${bundle.popular ? styles.buyBtnPrimary : ""}`}
                  onClick={() => handleBuy(bundle)}
                  disabled={purchasing === bundle.id}
                >
                  {purchasing === bundle.id ? "Processing…" : `Buy for R${bundle.price}`}
                </button>
              </div>
            ))}
          </div>
          <p className={styles.yocoNote}>
            Payments processed by <a href="https://www.yoco.com" target="_blank" rel="noopener noreferrer">Yoco</a> ·
            SSL encrypted · No card details stored by Gigscout
          </p>
        </section>

        {/* ── Transaction history ── */}
        <section>
          <h2 className={styles.sectionTitle}>Transaction History</h2>
          {loadingTx ? (
            <div className={styles.txLoading}>
              {[...Array(3)].map((_, i) => <div key={i} className={styles.txSkeleton} />)}
            </div>
          ) : transactions.length === 0 ? (
            <div className={styles.txEmpty}>No transactions yet.</div>
          ) : (
            <div className={styles.txList}>
              {transactions.map(tx => (
                <div key={tx.id} className={styles.txRow}>
                  <div className={styles.txIcon}>
                    {tx.type === "unlock"   ? <Unlock size={18} strokeWidth={2} /> :
                     tx.type === "purchase" ? <CreditCard size={18} strokeWidth={2} /> :
                                             <Gift size={18} strokeWidth={2} />}
                  </div>
                  <div className={styles.txBody}>
                    <div className={styles.txDesc}>{tx.description}</div>
                    <div className={styles.txDate}>{timeAgo(tx.createdAt)}</div>
                  </div>
                  <div className={`${styles.txAmount} ${tx.amount > 0 ? styles.txAmountPos : styles.txAmountNeg}`}>
                    {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </AppLayout>
  );
}
